import { useCallback, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import Menu from 'components/shared/menu';
import { LeftColumn, RightColumn } from 'components/shared/ResponsivePageLayout';
import Tabs, { TabType } from 'components/Tabs';
import { useProfile } from 'context/profile';
import useChatConversations from 'hooks/useChatConversations';
import useNow from 'hooks/useNow';
import useWorkflow from 'hooks/useWorkflow';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import { elapsed, simpleDateAtTime } from 'lib/date';
import { PERMISSIONS } from 'models/Profile';
import Workflow from 'models/Workflow';
import { ChatDraft } from 'services/api/chat';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, H2 } from 'styles/typography';
import ArrowLeft from 'svg/ArrowLeft';
import AttachFileIcon from 'svg/AttachFileIcon';
import EllipsisIcon from 'svg/EllipsisIcon';
import NetworkProviderIcon from 'svg/NetworkProviderIcon';
import PatientIcon from 'svg/PatientIcon';
import TrashIcon from 'svg/TrashIcon';

import ChatTab, { CHANNEL_PARAM } from './chat/ChatTab';
import UnreadBadge from './chat/UnreadBadge';
import EligibilityCheckSlideout from './eligibility/EligibilityCheckSlideout';
import { TASKS_ROUTES } from './tasks/constants';
import ActivityLog from './ActivityLog';
import ArchiveReferralModal from './ArchiveReferralModal';
import CategoryCard, { categoryAnchor } from './CategoryCard';
import { INBOUND_REFERRALS_ROUTES, isPortalWorkflow, REFERRAL_INTAKE_API_NAME } from './constants';
import FilesTab from './FilesTab';
import PatientSummaryChips from './PatientSummaryChips';
import ProgressRail from './ProgressRail';
import ReferralModal, { ReferralModalSection } from './ReferralModal';
import ReferralSentTo from './ReferralSentTo';

// The design names the patient ("Referral for Ismael Dunn was successfully archived."), but
// `patientName` falls back to "New referral" before extraction has produced any demographics —
// and archiving a referral in exactly that state is a normal thing to do, so it would read
// "Referral for New referral was successfully archived." Name the patient only when there is one.
function archivedToast(workflow: Workflow): string {
  return workflow.hasPatientName
    ? `Referral for ${workflow.patientName} was successfully archived.`
    : 'Referral was successfully archived.';
}

// The page's standard tab strip (components/Tabs, same as the patient story page). Files is last
// (OLIO-3234) so the order reads Referral Review → Chat → Activity Log → Files.
const PAGE_TABS: readonly TabType[] = [
  { label: 'Referral Review', value: 'review' },
  { label: 'Chat', value: 'chat' },
  { label: 'Activity Log', value: 'activity' },
  { label: 'Files', value: 'files' },
];

export default function InboundReferralPage() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { workflowId } = useParams<{ workflowId: string }>();
  const {
    workflow,
    isLoading,
    isError,
    completeActionAsync,
    completeActionsAsync,
    isCompletingActions,
    isCompletingAction,
    amendActionAsync,
    isAmendingAction,
    saveActionProgress,
    isSavingActionProgress,
    invokeAction,
    isInvokingAction,
    dispatchActionAsync,
    resendActionAsync,
    isDispatchingAction,
    updateValuesAsync,
    markActionViewed,
    archiveWorkflow,
    isArchivingWorkflow,
  } = useWorkflow(workflowId);
  const { addToast } = useToastActions();
  // Only referral-intake workflows have chat — portal automation/discovery runs (also
  // rendered by this page) get no Chat tab, channel fetch, or subscription. chatMessageView
  // (QA PIQ 5 on OLIO-2837): roles without it get no tab and no fetch — the API 403s the
  // same users, so this hides a surface that could only error.
  const hasChat = workflow?.definition.apiName === REFERRAL_INTAKE_API_NAME && profile.has(PERMISSIONS.chatMessageView);
  // Mounted at page level so the Chat tab and the tab badge read one cache from any tab.
  const {
    conversations: chatConversations,
    isLoading: isChatLoading,
    isError: isChatError,
  } = useChatConversations(hasChat ? workflowId : undefined);
  // The active tab lives in the URL (?tab=chat) so it survives refresh and deep-links;
  // a ?channel param with no tab param implies the Chat tab. The default tab stays out
  // of the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') ?? (searchParams.has('channel') ? 'chat' : null);
  // Tab resolution runs over the visible set, so a ?tab=chat deep link on a portal run
  // falls back to the default tab.
  const availableTabs = hasChat ? PAGE_TABS : PAGE_TABS.filter((pageTab) => pageTab.value !== 'chat');
  const tab = availableTabs.find((pageTab) => pageTab.value === tabParam) ?? PAGE_TABS[0];
  // The actively-viewed channel (chat tab only) — excluded from the badge displays below.
  const channelParam = searchParams.get(CHANNEL_PARAM);
  const viewedChannelId = tab.value === 'chat' ? channelParam : null;
  // On the chat tab before ChatTab canonicalizes ?channel (tab entry writes ?tab=chat alone),
  // the rollup would count the about-to-be-viewed default channel — suppress the tab badge
  // for that render instead of guessing which channel ChatTab will resolve.
  const showTabBadge = tab.value !== 'chat' || channelParam !== null;
  // null = closed; otherwise the modal opens in edit mode focused on this section.
  const [editingSection, setEditingSection] = useState<ReferralModalSection | null>(null);
  // Unsent chat drafts, per conversation — page-owned so they survive tab switches (the
  // Chat tab unmounts on a trip to Referral Review or the Activity Log).
  const [chatDrafts, setChatDrafts] = useState<Record<string, ChatDraft>>({});
  const setChatDraft = useCallback(
    (conversationId: string, value: ChatDraft) => setChatDrafts((prev) => ({ ...prev, [conversationId]: value })),
    []
  );
  // Which coverage's benefit verification the slideout is showing (design 008 §3e) — undefined
  // when closed, null on a referral with no coverages. It's the coverage rather than a plain
  // boolean because the values behind the slideout belong to one coverage, so it has to know
  // which one it opened for.
  const [eligibilityFacetId, setEligibilityFacetId] = useState<string | null | undefined>(undefined);
  const setTab = (next: TabType) => {
    const params = new URLSearchParams(searchParams);
    // The default tab stays out of the URL — unless a lingering ?channel would re-imply
    // chat. The channel param is kept so returning to Chat restores the selection.
    if (next.value === PAGE_TABS[0].value && !params.has('channel')) params.delete('tab');
    else params.set('tab', next.value);
    setSearchParams(params);
  };
  // The rail (OLIO-3234): switch to Review if another tab is active, then scroll once the tab
  // switch has actually flipped Layout's display back on — a category card in a display:none
  // ancestor has no scroll position for scrollIntoView to set. Also bumps that category's expand
  // signal, so a collapsed card the rail jumps to opens rather than scrolling to a closed header.
  const [expandSignals, setExpandSignals] = useState<Record<string, number>>({});
  const scrollToCategory = (key: string) => {
    setExpandSignals((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
    if (tab.value !== 'review') setTab(PAGE_TABS[0]);
    requestAnimationFrame(() => {
      document.getElementById(categoryAnchor(key))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const { canEdit } = useWorkflowPermissions();
  // The subline's "- N min ago" ticks on its own; server timestamps stay the source of truth.
  const now = useNow(30_000);

  // The Chat tab badge: the sum of unread counts across every visible facility except the
  // actively-viewed channel. Assumes the channel list is the complete visible set — if it
  // is ever paginated, derive this from the counts query instead (it stays whole-set).
  const totalUnread = chatConversations.reduce(
    (sum, conversation) => (conversation.id === viewedChannelId ? sum : sum + conversation.unreadCount),
    0
  );
  // Blue when any contributing channel's unread span mentions the viewer — the "you
  // specifically are wanted in Chat" signal, visible before the tab is opened.
  const anyMentioned = chatConversations.some(
    (conversation) => conversation.id !== viewedChannelId && conversation.unreadMentioned
  );
  const pageTabs = availableTabs.map((pageTab) => {
    if (pageTab.value === 'chat' && showTabBadge && totalUnread > 0)
      return { ...pageTab, affix: <UnreadBadge count={totalUnread} mentioned={anyMentioned} /> };
    // The count rides the workflow payload's own attachments list (same set the Files tab
    // fetches), so it costs no extra request — matching the `Files (N)` labels elsewhere.
    if (pageTab.value === 'files') return { ...pageTab, label: `Files (${workflow?.attachments.length ?? 0})` };
    return pageTab;
  });

  if (isLoading)
    return (
      <Centered>
        <CircularProgress color={colors.blue100} />
      </Centered>
    );
  if (isError || !workflow)
    return (
      <Centered>
        <Body>We couldn’t load this referral.</Body>
      </Centered>
    );

  // No in-app link lands a portal run here anymore, but the route still renders any run the
  // scope gate authorizes — and this page's portal accommodations are gone, so a direct URL
  // would get a blank title, an enabled edit menu, and an editable ReferralSentTo. Tasks is
  // where portal runs live now.
  if (isPortalWorkflow(workflow.definition.apiName)) return <Navigate to={TASKS_ROUTES.BASE} replace />;

  // Every affordance that opens ReferralModal goes through this one gate, and `openEditor` is
  // undefined when it's closed rather than a callback that no-ops: a component that can't be
  // handed an opener can't render a link that leads nowhere, so a new edit entry point has to
  // deal with the permission to compile (OLIO-2615). Portal runs redirected above, so no
  // per-type carve-out is needed here.
  const canEditReferral = canEdit && !workflow.isTerminal;
  const openEditor = canEditReferral ? setEditingSection : undefined;

  return (
    <Page>
      {editingSection && (
        <ReferralModal workflow={workflow} section={editingSection} onClose={() => setEditingSection(null)} />
      )}
      {confirmingArchive && (
        <ArchiveReferralModal
          onCancel={() => setConfirmingArchive(false)}
          isArchiving={isArchivingWorkflow}
          onConfirm={() =>
            archiveWorkflow(undefined, {
              onSuccess: () => {
                setConfirmingArchive(false);
                addToast({ text: archivedToast(workflow) });
                navigate(INBOUND_REFERRALS_ROUTES.BASE);
              },
            })
          }
        />
      )}
      <EligibilityCheckSlideout
        open={eligibilityFacetId !== undefined}
        onClose={() => setEligibilityFacetId(undefined)}
        workflow={workflow}
        facetId={eligibilityFacetId ?? null}
        onComplete={(actionId, outputs) => completeActionAsync({ actionId, outputs }).catch(() => undefined)}
        isCompleting={isCompletingAction}
        onInvoke={(actionId, intent) => invokeAction({ actionId, invokeAction: intent })}
        isInvoking={isInvokingAction}
        // 'insurance' rather than 'demographics': this prompt names what the check is missing —
        // Provider NPI, a member id — and all of it lives on the form's second step (OLIO-3384).
        onEditDetails={openEditor && (() => openEditor('insurance'))}
      />
      <Page.Header>
        <HeaderInner>
          <TitleRow>
            <BackButton type='button' onClick={() => navigate(INBOUND_REFERRALS_ROUTES.BASE)} aria-label='Back'>
              <ArrowLeft />
            </BackButton>
            <H2>{workflow.patientName}</H2>
            {canEditReferral && (
              <MenuWrap>
                <Menu $size='large'>
                  <Menu.Trigger>
                    <Button
                      variant='outline-gray'
                      style={{ width: 32, height: 32, padding: 0, flexShrink: 0 }}
                      data-testid='referral-actions'>
                      <EllipsisIcon />
                    </Button>
                  </Menu.Trigger>
                  <Menu.Content position='right' style={{ marginTop: 4 }}>
                    {/* One entry per thing that can be edited, matching the in-card links and the
                        modal titles they open (UX, 2026-08-05). A single "Edit" gave no clue which
                        of the screens it led to — so each carries the icon for the thing it edits
                        rather than three identical pencils. */}
                    <EditMenuItem data-testid='edit-referral' onClick={() => setEditingSection('documents')}>
                      <AttachFileIcon width={11} height={14} color={colors.black100} />
                      Add or remove documents
                    </EditMenuItem>
                    <EditMenuItem
                      data-testid='edit-demographics-menu'
                      onClick={() => setEditingSection('demographics')}>
                      <PatientIcon width={14} height={14} color={colors.black100} />
                      Edit demographics
                    </EditMenuItem>
                    {/* Same screen the Referral Sent To card's Edit link opens, so it carries the
                        same icon the card does — the card is where it's discovered, the menu is
                        where someone looks for every edit. */}
                    <EditMenuItem data-testid='edit-facilities-menu' onClick={() => setEditingSection('facilities')}>
                      <NetworkProviderIcon width={14} height={14} color={colors.black100} />
                      Edit facilities
                    </EditMenuItem>
                    {/* Last and separated: everything above edits the referral, this one ends it. */}
                    <Menu.Divider />
                    <ArchiveMenuItem data-testid='archive-referral' onClick={() => setConfirmingArchive(true)}>
                      <TrashIcon width={14} height={14} color={colors.red100} />
                      Archive referral
                    </ArchiveMenuItem>
                  </Menu.Content>
                </Menu>
              </MenuWrap>
            )}
          </TitleRow>
          {workflow.createdAt && (
            <Subline>
              Created on {simpleDateAtTime(workflow.createdAt)}
              {workflow.createdByName && ` by ${workflow.createdByName}`} - {elapsed(workflow.createdAt, now)} ago
            </Subline>
          )}
          <PatientSummaryChips workflow={workflow} />
        </HeaderInner>
        <TabRow>
          <Tabs tabs={pageTabs} selectedTab={tab} onTabClick={setTab} />
        </TabRow>
      </Page.Header>
      <Content $flush={tab.value === 'review'} $edgeToEdge={tab.value === 'chat'}>
        {tab.value === 'activity' && (
          <ActivityPane>
            <ActivityLog workflowId={workflow.id} isTerminal={workflow.isTerminal} />
          </ActivityPane>
        )}
        {tab.value === 'files' && (
          <FilesPane>
            {/* Keyed by the packet's ids: editing documents from the header menu leaves this tab
                mounted, and the remount refetches the list the moment the workflow payload's
                attachments change. */}
            <FilesTab key={workflow.attachments.map((attachment) => attachment.id).join()} workflowId={workflow.id} />
          </FilesPane>
        )}
        {tab.value === 'chat' && (
          <ChatTab
            conversations={chatConversations}
            isLoading={isChatLoading}
            isError={isChatError}
            drafts={chatDrafts}
            onDraftChange={setChatDraft}
          />
        )}
        {/* Hidden rather than unmounted: the decision panels hold in-progress local
            state (selections, notes) that a peek at the Activity Log must not wipe. */}
        <Layout style={{ display: tab.value === 'review' ? undefined : 'none' }}>
          <RailColumn>
            {/* Keyed by run: the route param can change without a remount, and the
                modal's open/search state must not leak across referrals. */}
            <ReferralSentTo key={workflow.id} workflow={workflow} editable={canEditReferral} />
            <ProgressRail workflow={workflow} onStepClick={scrollToCategory} />
          </RailColumn>

          <MainColumn>
            {/* onDispatch/onResend take the ASYNC variants, unlike the fire-and-forget writes beside
                them, because the sync modal awaits the outcome to decide whether to close. With
                `mutate` the promise resolved immediately and the modal closed even on a failed send —
                invisible while a refused dispatch still answered 200, and a lie the moment it
                answered 409. The modal catches and stays open; the toast carries the server's own
                message. */}
            {workflow.actionsByCategory().map(({ category, actions }) => (
              <CategoryCard
                key={category.key}
                category={category}
                actions={actions}
                workflow={workflow}
                expandSignal={expandSignals[category.key]}
                onComplete={(actionId, outputs) => completeActionAsync({ actionId, outputs }).catch(() => undefined)}
                onCompleteBatch={(actionIds, outputs) =>
                  completeActionsAsync({ actionIds, outputs }).catch(() => undefined)
                }
                onAmend={(actionId, outputs) => amendActionAsync({ actionId, outputs }).catch(() => undefined)}
                isCompleting={isCompletingAction || isAmendingAction || isCompletingActions}
                onSaveProgress={(actionId, outputs, options) =>
                  saveActionProgress({ actionId, outputs, narrationHint: options?.narrationHint })
                }
                isSavingProgress={isSavingActionProgress}
                onInvoke={(actionId, intent) => invokeAction({ actionId, invokeAction: intent })}
                isInvoking={isInvokingAction}
                onDispatch={(actionId, outputs) => dispatchActionAsync({ actionId, outputs })}
                onResend={(action, outputs) => resendActionAsync({ action, outputs })}
                isDispatching={isDispatchingAction}
                onUpdateValues={updateValuesAsync}
                onView={(actionId) => markActionViewed({ actionId })}
                onEdit={openEditor}
                onOpenEligibility={setEligibilityFacetId}
              />
            ))}
          </MainColumn>
        </Layout>
      </Content>
    </Page>
  );
}

const Centered = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const HeaderInner = styled.div`
  padding: 24px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const MenuWrap = styled.div`
  margin-left: auto;
`;

const EditMenuItem = styled(Menu.Item)`
  gap: 12px;
`;

const ArchiveMenuItem = styled(EditMenuItem)`
  color: ${({ theme }) => theme.colors.red100};
`;

// Height matches the H2 beside it so the arrow centres on the patient name rather than on the
// whole wrapping title row. Same arrow and same treatment as the detail slideouts' headers.
const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${({ theme }) => theme.textStyles.h2.lineHeight};
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${colors.black100};
`;

const Subline = styled(BodySmall)`
  color: ${colors.black50};
`;

// The tabs band under the header content, styled like PatientHeader's: a
// hairline above, and the header's own border-bottom closing beneath the
// active tab's underline.
const TabRow = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.black10};
  padding: 0 24px;
`;

const ActivityPane = styled.div`
  max-width: 1200px;
`;

const FilesPane = styled.div`
  max-width: 1200px;
`;

// Page.Content's shared margin-top/padding (24px top, 24px 24px 24px otherwise) is right for a
// plain content page, but on the review tab it doubled up with the columns' own spacing — flush
// 0/24px instead, with each column owning its own 24px bottom padding so it applies inside each
// independently-scrolling pane rather than to the row that holds them both. Only on the review
// tab — Activity Log has no such compensating padding of its own, so it keeps Page.Content's
// normal spacing. Local override rather than touching Page.Content itself, which every other
// page still wants unchanged.
//
// The chat tab goes one step further ($edgeToEdge, zero padding too): its rail is a full-bleed
// white panel flush against the global sidebar and the tab bar (Figma "Multi Building Access -
// Chat View"), so Page.Content's own 24px side padding has to go as well — ChatTab's rail and
// message pane each own their full 24px inset instead.
const Content = styled(Page.Content)<{ $flush: boolean; $edgeToEdge?: boolean }>`
  ${({ $flush }) =>
    $flush &&
    css`
      padding: 0 24px;
      margin-top: 0;
    `}

  ${({ $edgeToEdge }) =>
    $edgeToEdge &&
    css`
      padding: 0;
      margin-top: 0;
    `}
`;

// Two independently-scrolling columns split by a vertical divider, same structural pattern as
// the patient story page (LeftColumn/RightColumn, components/shared/ResponsivePageLayout) — the
// rail (Referral Sent To + Progress) stays in view while a long step list scrolls on its own.
// height:100% + min-height:0 is what lets each column compute a bounded height to scroll within
// instead of just growing to fit its content — dropping either breaks the independent scroll and
// falls back to one long page scroll, which is what happens below 1050px on purpose (see
// LeftColumn's own breakpoint): stacking two independently-scrolling panes on a small screen is
// worse than one normal scroll.
const Layout = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  height: 100%;

  @media (max-width: 1050px) {
    flex-direction: column;
    height: auto;
    gap: 36px;
  }
`;

// Same padding/divider/24px card gap as the patient story page's LeftColumn — only the width is
// narrower here (20%, not the patient story's 36%): the rail's own content (facility name,
// progress checklist) doesn't need that much room.
const RailColumn = styled(LeftColumn)`
  width: 360px;
  padding-bottom: 24px;

  @media (max-width: 1050px) {
    width: 100%;
  }
`;

const MainColumn = styled(RightColumn)`
  gap: 12px;
  max-width: 960px;
  padding-left: 36px;
  padding-right: 8px;
  padding-bottom: 24px;

  @media (max-width: 1050px) {
    padding-left: 0;
  }
`;
